import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import {
  KeyIcon, UserPlusIcon, CheckIcon, XMarkIcon, ClockIcon,
} from '@heroicons/react/24/outline';

/**
 * Who can sign in to the dashboard, and what they can reach.
 *
 * Only the owner sees this page, and only the owner can grant anything.
 * Anybody who can hand out access can hand themselves the scan alerts, which
 * is the one combination that defeats the point of having them.
 *
 * The security permissions are not on this page at all -- not greyed out,
 * absent. They cannot be granted by any role, any checkbox or any request;
 * the server refuses them regardless of what it is sent.
 */
export default function UsersPage() {
  const [users, setUsers]   = useState([]);
  const [roles, setRoles]   = useState([]);
  const [perms, setPerms]   = useState([]);
  const [audit, setAudit]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [err, setErr]       = useState('');
  const [busy, setBusy]     = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const [form, setForm] = useState({ email:'', name:'', role:'marketing', password:'' });

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await adminAPI.dashboardUsers();
      setUsers(r.users || []); setRoles(r.roles || []); setPerms(r.permissions || []);
    } catch (e) { setErr(e.message || 'Could not load.'); }
    finally { setLoad(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadAudit = async () => {
    try { const r = await adminAPI.usersAudit(); setAudit(r.audit || []); setShowAudit(true); }
    catch (e) { setErr(e.message || 'Could not read the log.'); }
  };

  const create = async () => {
    setBusy(true); setErr('');
    try {
      await adminAPI.createDashboardUser(form);
      setForm({ email:'', name:'', role:'marketing', password:'' });
      setAdding(false);
      await load();
    } catch (e) { setErr(e.message || 'Could not create the account.'); }
    finally { setBusy(false); }
  };

  const save = async (u, patch) => {
    setBusy(true); setErr('');
    try { await adminAPI.updateDashboardUser(u.id, patch); await load(); setEditing(null); }
    catch (e) { setErr(e.message || 'Could not save.'); }
    finally { setBusy(false); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`New dashboard password for ${u.email} (at least 10 characters):`);
    if (!pw) return;
    setBusy(true); setErr('');
    try { await adminAPI.setDashboardUserPassword(u.id, pw); setErr(''); alert('Password changed.'); }
    catch (e) { setErr(e.message || 'Could not set the password.'); }
    finally { setBusy(false); }
  };

  const groups = perms.reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p); return acc;
  }, {});

  return (
    <div style={{ padding:24, maxWidth:1100 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
        <KeyIcon style={{ width:26, height:26, color:'#1F4E79' }} />
        <h1 style={{ margin:0, fontSize:24 }}>Dashboard users</h1>
      </div>
      <p style={{ color:'var(--text-muted)', marginTop:0, maxWidth:740 }}>
        Who can sign in, and what they can reach. The security pages &mdash; scan
        alerts, settings and this page &mdash; belong to the owner account alone and
        cannot be granted to anybody, by any role.
      </p>

      {err && (
        <div style={{ background:'#FEF2F2', color:'#B91C1C', padding:'10px 14px',
                      borderRadius:8, marginBottom:16 }}>{err}</div>
      )}

      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <button className="btn-primary" onClick={() => setAdding(a => !a)}>
          <UserPlusIcon style={{ width:15, height:15 }} /> Add someone
        </button>
        <button className="btn-ghost" onClick={loadAudit}>
          <ClockIcon style={{ width:15, height:15 }} /> Who changed what
        </button>
      </div>

      {adding && (
        <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:12,
                      padding:18, marginBottom:20 }}>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:12 }}>
            <input placeholder="Full name" value={form.name}
                   onChange={e => setForm(f => ({ ...f, name:e.target.value }))}
                   style={inp(2)} />
            <input placeholder="Email" value={form.email}
                   onChange={e => setForm(f => ({ ...f, email:e.target.value }))}
                   style={inp(2)} />
            <input placeholder="Password (10+ characters)" type="text" value={form.password}
                   onChange={e => setForm(f => ({ ...f, password:e.target.value }))}
                   style={inp(2)} />
            <select value={form.role}
                    onChange={e => setForm(f => ({ ...f, role:e.target.value }))}
                    style={inp(1)}>
              {roles.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          {roles.find(r => r.key === form.role) && (
            <p style={{ fontSize:13, color:'var(--text-muted)', margin:'0 0 12px' }}>
              {roles.find(r => r.key === form.role).description}
            </p>
          )}
          <button className="btn-primary" disabled={busy} onClick={create}>Create</button>
          <button className="btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
        </div>
      )}

      {loading ? <p style={{ color:'var(--text-muted)' }}>Loading…</p> : users.map(u => (
        <div key={u.id} style={{
          background:'#fff', border:'1px solid var(--border)', borderRadius:12,
          padding:'14px 18px', marginBottom:10,
          opacity: u.isActive ? 1 : 0.55,
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontWeight:600 }}>
                {u.name}
                {u.role === 'superadmin' && (
                  <span style={{ marginLeft:8, fontSize:11, background:'#1F4E79', color:'#fff',
                                 padding:'2px 8px', borderRadius:20 }}>Owner</span>
                )}
                {u.custom && (
                  <span style={{ marginLeft:8, fontSize:11, background:'#FEF3C7', color:'#92400E',
                                 padding:'2px 8px', borderRadius:20 }}>Custom access</span>
                )}
              </div>
              <div style={{ fontSize:13, color:'var(--text-muted)' }}>
                {u.email} · {u.roleLabel}
                {u.lastLogin && <> · last in {format(new Date(u.lastLogin), 'dd MMM HH:mm')}</>}
              </div>
            </div>
            {u.role !== 'superadmin' && (
              <div style={{ display:'flex', gap:6, alignItems:'flex-start' }}>
                <button className="btn-ghost" disabled={busy}
                        onClick={() => setEditing(editing === u.id ? null : u.id)}>
                  {editing === u.id ? 'Close' : 'Access'}
                </button>
                <button className="btn-ghost" disabled={busy} onClick={() => resetPassword(u)}>
                  Password
                </button>
                <button className="btn-ghost" disabled={busy}
                        onClick={() => save(u, { isActive: !u.isActive })}>
                  {u.isActive
                    ? <><XMarkIcon style={{ width:14, height:14 }} /> Suspend</>
                    : <><CheckIcon style={{ width:14, height:14 }} /> Restore</>}
                </button>
              </div>
            )}
          </div>

          {editing === u.id && (
            <div style={{ marginTop:14, borderTop:'1px solid var(--border)', paddingTop:14 }}>
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:13, fontWeight:600, marginRight:8 }}>Role</label>
                <select
                  defaultValue={u.role}
                  onChange={e => save(u, { role: e.target.value, permissions: [] })}
                  style={{ padding:'7px 10px', border:'1px solid var(--border)', borderRadius:8 }}
                >
                  {roles.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
                <span style={{ fontSize:12, color:'var(--text-subtle)', marginLeft:10 }}>
                  Changing the role resets their access to that role&rsquo;s default.
                </span>
              </div>

              <p style={{ fontSize:13, color:'var(--text-muted)', margin:'0 0 10px' }}>
                Or tick exactly what this person may reach. Anything ticked here overrides
                the role.
              </p>
              {Object.entries(groups).map(([group, list]) => (
                <div key={group} style={{ marginBottom:10 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--text-subtle)',
                                textTransform:'uppercase', letterSpacing:0.4 }}>{group}</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 16px', marginTop:4 }}>
                    {list.map(p => (
                      <label key={p.key} style={{ fontSize:13, display:'flex', gap:5,
                                                  alignItems:'center', minWidth:180 }}>
                        <input
                          type="checkbox"
                          defaultChecked={u.permissions.includes(p.key)}
                          onChange={e => {
                            const next = e.target.checked
                              ? [...new Set([...u.permissions, p.key])]
                              : u.permissions.filter(x => x !== p.key);
                            u.permissions = next;
                          }}
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <button className="btn-primary" disabled={busy}
                      onClick={() => save(u, { permissions: u.permissions })}>
                Save access
              </button>
            </div>
          )}
        </div>
      ))}

      {showAudit && (
        <section style={{ marginTop:28 }}>
          <h2 style={{ fontSize:16 }}>Who changed what</h2>
          <p style={{ fontSize:13, color:'var(--text-muted)', marginTop:0 }}>
            Access changes are the first thing somebody would make and the last thing
            anybody notices.
          </p>
          {audit.length === 0 ? (
            <p style={{ color:'var(--text-subtle)', fontSize:13 }}>Nothing yet.</p>
          ) : (
            <table style={{ width:'100%', fontSize:13, background:'#fff',
                            border:'1px solid var(--border)', borderRadius:10 }}>
              <tbody>
                {audit.map((a, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'8px 12px', whiteSpace:'nowrap',
                                 color:'var(--text-muted)' }}>
                      {format(new Date(a.at), 'dd MMM HH:mm')}
                    </td>
                    <td style={{ padding:'8px 12px' }}>{a.actor_email}</td>
                    <td style={{ padding:'8px 12px', fontFamily:'monospace' }}>{a.action}</td>
                    <td style={{ padding:'8px 12px' }}>{a.target_email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}

const inp = (flex) => ({
  flex: `${flex} 1 180px`, minWidth:0, padding:'9px 12px',
  border:'1px solid var(--border)', borderRadius:8, fontSize:14,
});
